// CORE
// Este é um arquivo core do boilerplate e modificações neste arquivo serão bloqueadas via pré-hook de commit.
// Fazemos este bloqueio para manter a consistência do boilerplate e diminuir as chances de conflitos nos updates.
// Caso possível, procure uma alternativa ao uso deste arquivo.
// Se a alteração for realmente necessária ou for um update do boilerplate,
// realize o commit usando o comando 'git commit --no-verify'.
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-console */

import {
  QueryClient,
  QueryKey as ReactQueryKey,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  create,
  keyResolver,
  windowedFiniteBatchScheduler,
} from '@yornaath/batshit';
import axios from 'axios';
import { useEffect, useMemo } from 'react';
console.log(
  'process.env.NEXT_PUBLIC_API_URL',
  process.env.NEXT_PUBLIC_API_URL,
);
const baseUrl = process.env.NEXT_PUBLIC_API_URL || '';
const batchers: Record<string, any> = {};

/**
 * Builds a URL from base URL, path parameters, and query parameters.
 * @param baseUrl - The base URL of the API.
 * @param pathParamsArr - Array of path segments.
 * @param queryParamsArr - Object of query parameters.
 * @returns The constructed URL.
 */
export function buildURL(
  baseUrl: string,
  pathParamsArr: string[],
  queryParamsArr: Record<string, any>,
): string {
  const url = new URL(baseUrl);
  const pathSegments = pathParamsArr
    .filter((param) => ['string', 'number'].includes(typeof param))
    .map((param) => param.toString());

  url.pathname = url.pathname.replace(/\/?$/, '/') + pathSegments.join('/');
  if (pathSegments.length > 0) {
    url.pathname += '/';
  }
  const searchParams = new URLSearchParams();
  Object.entries(queryParamsArr).forEach(([key, value]) => {
    if (value !== undefined && value != null) {
      if (Array.isArray(value)) {
        if (value.length) {
          searchParams.append(key, value.join(','));
        }
      } else {
        searchParams.append(key, value.toString());
      }
    }
  });

  const queryString = searchParams.toString();
  if (queryString) {
    url.search = queryString;
  }
  return url.toString().replace(/%2C/g, ',');
}

/**
 * Smart default fetch function that automatically uses batch fetching when appropriate.
 * If the queryKey ends with what looks like an ID, it will use createBatchFetch.
 * Otherwise, it falls back to defaultQueryFetch.
 * @param options - Fetch options.
 * @param options.queryKey - The query key array.
 * @param options.queryParams - Query parameters.
 * @returns The response data.
 */
export async function smartDefaultFetch<T>({
  queryKey,
  queryParams = {},
}: {
  queryKey: (string | number)[];
  queryParams?: Record<string, any>;
}): Promise<T> {
  const lastElement = queryKey[queryKey.length - 1];
  const hasIdLikeLastElement =
    lastElement !== undefined &&
    (typeof lastElement === 'number' ||
      (typeof lastElement === 'string' && /^[0-9]+$/.test(lastElement)));
  if (queryKey.length > 1 && hasIdLikeLastElement) {
    const baseQueryKey = queryKey.slice(0, -1);
    const batchFetch = createBatchFetch<T & { id: string | number }>(
      baseQueryKey,
      queryParams,
    );
    return batchFetch({ queryKey, queryParams });
  }
  return defaultQueryFetch<T>({ queryKey, queryParams });
}

/**
 * Default fetch function for queries.
 * Performs a GET request to the constructed URL.
 * @param options - Fetch options.
 * @param options.queryKey - The query key array.
 * @param options.queryParams - Query parameters.
 * @returns The response data.
 */
export async function defaultQueryFetch<T>({
  queryKey,
  queryParams = {},
}: {
  queryKey: (string | number)[];
  queryParams?: Record<string, any> | null | undefined;
}): Promise<T> {
  const stringQueryKey = queryKey.map(String);
  const url = buildURL(baseUrl, stringQueryKey, queryParams ?? {});
  const response = await axios.get<T>(url, { withCredentials: true });
  return response.data;
}

/**
 * Creates a batched fetch function for individual items under a base query key.
 * Uses the batshit library to batch requests.
 * @param baseQueryKey - The base query key for batching (e.g., ['posts', 1, 'comments']).
 * @param baseQueryParams - Optional base query parameters to include with every batch request.
 * @returns A fetch function that batches requests by ID.
 */
export function createBatchFetch<T extends { id: string | number }>(
  baseQueryKey: (string | number)[],
  baseQueryParams: Record<string, any> = {},
) {
  const stringBaseQueryKey = baseQueryKey.map(String);
  const batcherKey = `${stringBaseQueryKey.join('/')}-${JSON.stringify(baseQueryParams)}`;
  let batcher = batchers[batcherKey];
  if (!batcher) {
    batcher = create({
      fetcher: async (ids: string[]) => {
        const queryParams = { ...baseQueryParams, id: ids };
        const response = await defaultQueryFetch<{ results: T[] } | T[]>({
          queryKey: baseQueryKey,
          queryParams,
        });
        return Array.isArray(response) ? response : response?.results || [];
      },
      resolver: keyResolver('id'),
      scheduler: windowedFiniteBatchScheduler({
        windowMs: 40,
        maxBatchSize: 200,
      }),
    });
    batchers[batcherKey] = batcher;
  }
  return async ({
    queryKey,
    queryParams = {},
  }: {
    queryKey: (string | number)[];
    queryParams?: Record<string, any>;
  }): Promise<T> => {
    const id = String(queryKey[queryKey.length - 1]);
    const paramsMatch =
      Object.keys(baseQueryParams).every(
        (key) => baseQueryParams[key] === queryParams[key],
      ) &&
      Object.keys(queryParams).every(
        (key) => baseQueryParams[key] === queryParams[key] || key === 'id',
      );

    if (!paramsMatch) {
      return defaultQueryFetch<T>({ queryKey, queryParams });
    }

    const result = await batcher.fetch(id);
    if (!result) {
      throw new Error(
        `Item with id ${id} not found for queryKey ${batcherKey}`,
      );
    }
    return result;
  };
}

/**
 * Creates a batched fetch function for queries based on a specific query parameter.
 * @param baseQueryKey - The base query key (e.g., ['posts']).
 * @param batchParam - The query parameter to batch on (e.g., 'author').
 * @param resultKey - The key in the result to match against (defaults to batchParam).
 * @returns A fetch function that batches requests by the specified parameter.
 */
export function createParamBatchFetch<T>(
  baseQueryKey: (string | number)[],
  batchParam: string,
  resultKey: string = batchParam,
) {
  const stringBaseQueryKey = baseQueryKey.map(String);
  const batcherKey = `${stringBaseQueryKey.join('/')}-${batchParam}`;
  let batcher = batchers[batcherKey];

  if (!batcher) {
    batcher = create({
      fetcher: async (paramValues: string[]) => {
        const queryParams = { [batchParam]: paramValues };
        const response = await defaultQueryFetch<{ results: T[] }>({
          queryKey: baseQueryKey,
          queryParams,
        });
        return response.results || (response as any);
      },
      resolver: (results: T[], paramValue: string) => {
        return results.filter((result) => {
          const value = result?.[resultKey as keyof T];
          if (value === undefined) return false;
          return Array.isArray(value)
            ? value.includes(paramValue)
            : String(value) === paramValue;
        });
      },
      scheduler: windowedFiniteBatchScheduler({
        windowMs: 40,
        maxBatchSize: 200,
      }),
    });
    batchers[batcherKey] = batcher;
  }

  return async ({
    queryParams,
  }: {
    queryParams: Record<string, any>;
  }): Promise<T[]> => {
    const paramValue = queryParams[batchParam];
    return batcher.fetch(String(paramValue));
  };
}

/**
 * Default fetch function for mutations.
 * Performs the appropriate HTTP request based on the method.
 * @param options - Mutation options.
 * @param options.queryKey - The query key array.
 * @param options.data - The mutation data.
 * @param options.method - The HTTP method (POST, PUT, PATCH, DELETE).
 * @returns The response data.
 */
async function defaultMutationFetch<T, U>({
  queryKey,
  data,
  method,
}: {
  queryKey: (string | number)[];
  data: T;
  method: string;
}): Promise<U> {
  const stringQueryKey = queryKey.map(String);
  const url = buildURL(baseUrl, stringQueryKey, {});

  return axios<U>({
    method,
    url,
    data,
    withCredentials: true,
  }).then((response) => response.data);
}

/**
 * Mutation fetch function that automatically adds the ID from the data to the query key.
 * For example, with queryKey=['users'] and data={id: 123, name: 'John'},
 * the actual request will be made to /users/123.
 * @param options - Mutation options.
 * @param options.queryKey - The query key array.
 * @param options.data - The mutation data.
 * @param options.method - The HTTP method (POST, PUT, PATCH, DELETE).
 * @returns The response data.
 */
async function mutationFetchAddId<T extends { id?: string | number }, U>({
  queryKey,
  data,
  method,
}: {
  queryKey: (string | number)[];
  data: T;
  method: string;
}): Promise<U> {
  const id = data?.id;

  const fullQueryKey = id !== undefined ? [...queryKey, id] : queryKey;

  return defaultMutationFetch<T, U>({
    queryKey: fullQueryKey,
    data,
    method,
  });
}

/**
 * Mutation fetch function that interpolates path parameters from data.
 * For example, with queryKey=['users', ':userId', 'posts', ':postId'] and data={userId: 123, postId: 456},
 * the actual request will be made to /users/123/posts/456.
 * @param options - Mutation options.
 * @param options.queryKey - The query key array with placeholders.
 * @param options.data - The mutation data.
 * @param options.method - The HTTP method (POST, PUT, PATCH, DELETE).
 * @returns The response data.
 */
async function mutationFetchInterpolate<T, U>({
  queryKey,
  data,
  method,
}: {
  queryKey: (string | number)[];
  data: T;
  method: string;
}): Promise<U> {
  const interpolatedQueryKey = queryKey.map((segment) => {
    if (typeof segment === 'string' && segment.startsWith(':')) {
      const variableName = segment.substring(1);

      const value = (data as any)?.[variableName];
      return value !== undefined ? String(value) : segment;
    }
    return String(segment);
  });

  return defaultMutationFetch<T, U>({
    queryKey: interpolatedQueryKey,
    data,
    method,
  });
}

/**
 * Default no-op cache handler for queries. UseQuery handles standard caching.
 */
/* eslint-disable-next-line no-empty-function */
function noOpCacheHandler() {}

/**
 * Cache handler that processes array query responses (including paginated).
 * Stores individual items from the array in the cache with their own query keys (listQueryKey + item.id).
 * Does NOT modify the original query data for the list itself (useQuery handles that).
 * @param args - Cache arguments.
 * @param args.queryClient - The react-query client.
 * @param args.listQueryKey - The base query key for the list (e.g., ['users']).
 * @param args.data - The array response data (potentially paginated like { results: T[] } or just T[]).
 */
export function updateCacheWithListItems<T extends { id?: string | number }>({
  queryClient,
  listQueryKey,
  data,
}: {
  queryClient: QueryClient;
  listQueryKey: ReactQueryKey;
  data: any;
}) {
  if (!data) return;

  let items: T[] = [];
  if (data && data.results && Array.isArray(data.results)) {
    items = data.results;
  } else if (Array.isArray(data)) {
    items = data;
  }
  items.forEach((item: T) => {
    if (item && item.id !== undefined && item.id !== null) {
      const itemQueryKey = [...listQueryKey, item.id];
      queryClient.setQueryData(itemQueryKey, item);
    }
  });
}

/**
 * Default cache handler for mutations.
 * Updates the cache based on the mutation method.
 * @param args - Cache arguments.
 * @param args.queryClient - The react-query client.
 * @param args.listQueryKey - The query key for the list/collection (e.g., ['users']).
 * @param args.data - The response data from the mutation.
 * @param args.variables - The mutation variables (input data).
 * @param args.method - The HTTP method.
 */
function defaultMutationCacheHandler<
  T extends { id?: string | number },
  U extends { id?: string | number },
>({
  queryClient,
  listQueryKey,
  data,
  variables,
  method,
}: {
  queryClient: QueryClient;
  listQueryKey: ReactQueryKey;
  data: U | undefined;
  variables: T;
  method: string;
}) {
  const id = variables?.id ?? data?.id;
  const itemQueryKey = id !== undefined ? [...listQueryKey, id] : undefined;
  if (method === 'POST' && data?.id) {
    queryClient.setQueryData([...listQueryKey, data.id], data);
    queryClient.invalidateQueries({ queryKey: listQueryKey, exact: false });
  } else if (
    (method === 'PUT' || method === 'PATCH') &&
    itemQueryKey &&
    data
  ) {
    queryClient.setQueryData(itemQueryKey, data);
    queryClient.invalidateQueries({ queryKey: listQueryKey, exact: false });
  } else if (method === 'DELETE' && itemQueryKey) {
    queryClient.removeQueries({ queryKey: itemQueryKey, exact: true });
    queryClient.invalidateQueries({ queryKey: listQueryKey, exact: false });
  } else {
    queryClient.invalidateQueries({ queryKey: listQueryKey, exact: false });
  }
}
type BaseQueryKey = (string | number)[];
type QueryCacheBehaviorFunction<TData> = (args: {
  queryClient: QueryClient;
  /** The final query key used by useQuery (including queryParams) */
  queryKey: ReactQueryKey;
  /** The base query key (without queryParams) */
  listQueryKey: BaseQueryKey;
  data: TData;
}) => void;
type OptimisticUpdateContext<TItem, TList> = {
  previousItemData?: TItem;
  previousListData?: TList;
  optimisticItemId?: string | number;
};

/**
 * Creates onMutate and onError handlers for optimistic updates.
 * Handles cache manipulation (cancellation, snapshotting, optimistic set, rollback).
 * Does NOT handle side effects like notifications - those should be added separately.
 *
 * @param queryClient - The QueryClient instance.
 * @param listQueryKey - The base query key for the list/collection.
 * @param method - The mutation method ('POST', 'PUT', 'PATCH', 'DELETE').
 * @param getListQueryKey - Optional function to get the specific list query key (e.g., including filters/pagination) if different from listQueryKey.
 * @returns An object containing onMutate and onError handlers compatible with useApiMutation.
 */
export function createOptimisticUpdateHandlers<
  TVariables extends { id?: string | number },
  TData extends { id?: string | number },
  TListData = unknown,
  TError = Error,
>(
  queryClient: QueryClient,
  listQueryKey: BaseQueryKey,
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  getListQueryKey?: (vars: TVariables) => ReactQueryKey,
) {
  const onMutate = async (
    variables: TVariables,
  ): Promise<OptimisticUpdateContext<TData, TListData> | undefined> => {
    const id = variables?.id;
    const itemQueryKey = id !== undefined ? [...listQueryKey, id] : undefined;
    const specificListQueryKey = getListQueryKey
      ? getListQueryKey(variables)
      : listQueryKey;
    await queryClient.cancelQueries({
      queryKey: specificListQueryKey,
      exact: false,
    });
    if (itemQueryKey) {
      await queryClient.cancelQueries({ queryKey: itemQueryKey, exact: true });
    }
    const previousItemData = itemQueryKey
      ? queryClient.getQueryData<TData>(itemQueryKey)
      : undefined;
    const previousListData = queryClient.getQueryData<TListData>(
      specificListQueryKey,
    );

    const optimisticItemId: string | number | undefined = id;
    try {
      if (method === 'POST') {
        console.warn(
          'Optimistic POST update not fully implemented in helper. Consider list invalidation on success.',
        );
      } else if ((method === 'PUT' || method === 'PATCH') && itemQueryKey) {
        queryClient.setQueryData<TData | undefined>(itemQueryKey, (oldData) =>
          oldData
            ? { ...oldData, ...variables }
            : ({ ...variables } as unknown as TData),
        );
      } else if (method === 'DELETE' && itemQueryKey) {
        queryClient.removeQueries({ queryKey: itemQueryKey, exact: true });
      }
    } catch (error) {
      console.error('Error during optimistic update:', error);
      if (itemQueryKey && previousItemData !== undefined) {
        queryClient.setQueryData(itemQueryKey, previousItemData);
      }
      if (previousListData !== undefined) {
        queryClient.setQueryData(specificListQueryKey, previousListData);
      }
      throw error;
    }
    return { previousItemData, previousListData, optimisticItemId };
  };

  const onError = (
    error: TError,
    variables: TVariables,
    context?: OptimisticUpdateContext<TData, TListData>,
  ) => {
    console.error('Mutation failed, rolling back optimistic update:', error);
    const id = variables?.id ?? context?.optimisticItemId;
    const itemQueryKey = id !== undefined ? [...listQueryKey, id] : undefined;
    const specificListQueryKey = getListQueryKey
      ? getListQueryKey(variables)
      : listQueryKey;

    if (context?.previousItemData !== undefined && itemQueryKey) {
      queryClient.setQueryData(itemQueryKey, context.previousItemData);
    } else if (itemQueryKey && method !== 'POST') {
      queryClient.removeQueries({ queryKey: itemQueryKey, exact: true });
    }
    if (context?.previousListData !== undefined) {
      queryClient.setQueryData(specificListQueryKey, context.previousListData);
    } else {
      queryClient.invalidateQueries({ queryKey: specificListQueryKey });
    }
  };

  return { onMutate, onError };
}

/**
 * Hook for performing API queries.
 * @template TData The type of data expected from the query.
 * @param options - Query options.
 * @param options.queryKey - The base query key (e.g., ['users']). This is used as the `listQueryKey` for cache operations.
 * @param options.queryParams - Query parameters for filtering, pagination etc. (optional). These are appended to the base query key to form the final `queryKey` for `useQuery`.
 * @param options.fetchFn - Custom fetch function (optional). Defaults to `smartDefaultFetch` which automatically uses batch fetching when appropriate.
 * @param options.cacheBehavior - A function to perform custom cache operations after a successful query (optional). Defaults to a no-op. Use `updateCacheWithListItems` for array/list caching.
 * @param options - Additional react-query options (`UseQueryOptions`).
 * @returns The query result from `useQuery`.
 */
export function useApiQuery<TData>({
  queryKey,
  queryParams = {},
  fetchFn = (args) => smartDefaultFetch<TData>(args as any),
  cacheBehavior = noOpCacheHandler,
  ...reactQueryOptions
}: {
  queryKey: BaseQueryKey;
  queryParams?: Record<string, any>;
  fetchFn?: (args: {
    queryKey: BaseQueryKey;
    queryParams: Record<string, any>;
  }) => Promise<TData>;
  cacheBehavior?: QueryCacheBehaviorFunction<TData>;
} & Omit<
  UseQueryOptions<TData, Error, TData, ReactQueryKey>,
  'queryKey' | 'queryFn'
>): UseQueryResult<TData, Error> {
  const queryClient = useQueryClient();
  const finalQueryKey = useMemo<ReactQueryKey>(() => {
    return Object.keys(queryParams ?? {}).length > 0
      ? [...queryKey, queryParams]
      : queryKey;
  }, [JSON.stringify(queryKey), JSON.stringify(queryParams)]);

  const queryResult = useQuery<TData, Error, TData, ReactQueryKey>({
    queryKey: finalQueryKey,
    queryFn: () => fetchFn({ queryKey, queryParams }),
    ...reactQueryOptions,
  });
  useEffect(() => {
    if (queryResult.isSuccess && queryResult.data) {
      cacheBehavior({
        queryClient,
        queryKey: finalQueryKey,
        listQueryKey: queryKey,
        data: queryResult.data,
      });
    }
  }, [
    queryResult.data,
    queryResult.isSuccess,
    queryClient,
    finalQueryKey,
    JSON.stringify(queryKey),
    cacheBehavior,
  ]);

  return queryResult;
}

/**
 * Hook for performing API mutations.
 * @param options - Mutation options.
 * @param options.listQueryKey - The base query key for the list/collection being mutated (e.g., ['users']). Required for cache invalidation/updates.
 * @param options.fetchFn - Custom fetch function (optional). Uses method-specific defaults if not provided.
 * @param options.cacheFn - Custom cache handler function (optional). Uses default based on method if not provided.
 * @param options.method - The HTTP method (POST, PUT, PATCH, DELETE). Defaults to 'POST'.
 * @param options.onMutate - Optional callback before mutation (e.g., for optimistic updates).
 * @param options.onSuccess - Optional callback on mutation success.
 * @param options.onError - Optional callback on mutation error (e.g., for rollback, notifications).
 * @param options.onSettled - Optional callback after mutation finishes (success or error).
 * @param otherMutationOptions - Additional standard react-query mutation options (UseMutationOptions).
 * @returns The mutation result from useMutation.
 */
export function useApiMutation<
  TVariables extends { id?: string | number } | any,
  TData extends { id?: string | number } | any,
  TError = Error,
  TContext = OptimisticUpdateContext<TData, unknown> | unknown,
>({
  listQueryKey,
  fetchFn,
  cacheFn = (args) => defaultMutationCacheHandler(args as any),
  method = 'POST',
  onMutate: customOnMutate,
  onError: customOnError,
  onSuccess: customOnSuccess,
  onSettled: customOnSettled,
  ...otherMutationOptions
}: {
  listQueryKey: BaseQueryKey;
  fetchFn?: (args: {
    queryKey: BaseQueryKey;
    data: TVariables;
    method: string;
  }) => Promise<TData>;
  cacheFn?: (args: {
    queryClient: QueryClient;
    listQueryKey: BaseQueryKey;
    data: TData | undefined;
    variables: TVariables;
    method: string;
    context?: TContext;
  }) => void;
  method?: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  onMutate?: UseMutationOptions<
    TData,
    TError,
    TVariables,
    TContext
  >['onMutate'];
  onError?: UseMutationOptions<TData, TError, TVariables, TContext>['onError'];
  onSuccess?: UseMutationOptions<
    TData,
    TError,
    TVariables,
    TContext
  >['onSuccess'];
  onSettled?: UseMutationOptions<
    TData,
    TError,
    TVariables,
    TContext
  >['onSettled'];
} & Omit<
  UseMutationOptions<TData, TError, TVariables, TContext>,
  'mutationFn' | 'onMutate' | 'onError' | 'onSuccess' | 'onSettled'
>): ReturnType<typeof useMutation<TData, TError, TVariables, TContext>> {
  const queryClient = useQueryClient();
  const mutationFetchByMethod = {
    POST: defaultMutationFetch,
    PUT: mutationFetchAddId,
    PATCH: mutationFetchAddId,
    DELETE: mutationFetchAddId,
  };
  const finalFetchFn = fetchFn ?? mutationFetchByMethod[method];

  return useMutation<TData, TError, TVariables, TContext>({
    mutationFn: (variables) =>
      finalFetchFn({
        queryKey: listQueryKey,
        data: variables,
        method,
      }),
    onMutate: customOnMutate,
    onSuccess: (data, variables, context) => {
      cacheFn({ queryClient, listQueryKey, data, variables, method, context });
      customOnSuccess?.(data, variables, context);
    },
    onError: customOnError,
    onSettled: customOnSettled,
    ...otherMutationOptions,
  });
}
